/**
 * Evolution Tracker
 * 
 * Tracks user's spiritual journey and personal growth over time
 * - Meditation minutes
 * - Journal entries
 * - Sacred geometry creations
 * - Audio experiments
 * - Wisdom readings
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Calendar, Award, Flame, Heart, Book, Music, Sparkles } from 'lucide-react';

interface EvolutionData {
  meditationMinutes: number;
  meditationSessions: number;
  journalEntries: number;
  wisdomReadings: number;
  audioExperiments: number;
  geometryCreations: number;
  currentStreak: number;
  longestStreak: number;
  level: number;
  experience: number;
  lastActive: number;
}

const EvolutionTracker: React.FC = () => {
  const [evolution, setEvolution] = useState<EvolutionData>({
    meditationMinutes: 0,
    meditationSessions: 0,
    journalEntries: 0,
    wisdomReadings: 0,
    audioExperiments: 0,
    geometryCreations: 0,
    currentStreak: 0,
    longestStreak: 0,
    level: 1,
    experience: 0,
    lastActive: Date.now()
  });

  // Load evolution data
  useEffect(() => {
    try {
      const saved = localStorage.getItem('fr00t_evolution');
      if (saved) {
        setEvolution(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load evolution data:', error);
    }
  }, []);

  // Calculate level from experience
  const calculateLevel = (exp: number) => {
    return Math.floor(Math.sqrt(exp / 100)) + 1;
  };

  const experienceForNextLevel = (level: number) => {
    return level * level * 100;
  };

  const stats = [
    {
      icon: Flame,
      label: 'Current Streak',
      value: evolution.currentStreak,
      unit: 'days',
      color: '#f59e0b'
    },
    {
      icon: Heart,
      label: 'Meditation Time',
      value: evolution.meditationMinutes,
      unit: 'minutes',
      color: '#8b5cf6'
    },
    {
      icon: Book,
      label: 'Journal Entries',
      value: evolution.journalEntries,
      unit: 'entries',
      color: '#6366f1'
    },
    {
      icon: Music,
      label: 'Audio Sessions',
      value: evolution.audioExperiments,
      unit: 'sessions',
      color: '#10b981'
    },
    {
      icon: Sparkles,
      label: 'Sacred Geometry',
      value: evolution.geometryCreations,
      unit: 'creations',
      color: '#f43f5e'
    },
    {
      icon: TrendingUp,
      label: 'Wisdom Gained',
      value: evolution.wisdomReadings,
      unit: 'readings',
      color: '#06b6d4'
    }
  ];

  const currentLevel = calculateLevel(evolution.experience);
  const nextLevelExp = experienceForNextLevel(currentLevel);
  const progressPercent = (evolution.experience % nextLevelExp) / nextLevelExp * 100;

  return (
    <div style={{ width: '100%', maxWidth: '100%', margin: '0 auto' }}>
      {/* Level & Experience */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(99, 102, 241, 0.2))',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem',
          border: '2px solid rgba(139, 92, 246, 0.3)',
          textAlign: 'center'
        }}
      >
        <div style={{
          fontSize: '3rem',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '0.5rem'
        }}>
          Level {currentLevel}
        </div>
        
        <div style={{
          fontSize: '0.875rem',
          color: '#64748b',
          marginBottom: '1rem'
        }}>
          {evolution.experience} / {nextLevelExp} XP
        </div>

        <div style={{
          width: '100%',
          maxWidth: '400px',
          margin: '0 auto',
          height: '12px',
          background: 'rgba(100, 116, 139, 0.2)',
          borderRadius: '6px',
          overflow: 'hidden'
        }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, #8b5cf6, #6366f1)',
              borderRadius: '6px'
            }}
          />
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              style={{
                background: 'rgba(30, 41, 59, 0.9)',
                borderRadius: '12px',
                padding: '1.5rem',
                border: `2px solid ${stat.color}40`,
                textAlign: 'center'
              }}
            >
              <Icon size={32} color={stat.color} style={{ margin: '0 auto 0.75rem' }} />
              
              <div style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: stat.color,
                marginBottom: '0.25rem'
              }}>
                {stat.value}
              </div>
              
              <div style={{
                fontSize: '0.75rem',
                color: '#64748b',
                marginBottom: '0.25rem'
              }}>
                {stat.unit}
              </div>
              
              <div style={{
                fontSize: '0.875rem',
                color: '#f1f5f9',
                fontWeight: '600'
              }}>
                {stat.label}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          marginTop: '2rem',
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '2px solid rgba(139, 92, 246, 0.2)',
          textAlign: 'center'
        }}
      >
        <Calendar size={32} color="#8b5cf6" style={{ margin: '0 auto 0.75rem' }} />
        
        <h4 style={{
          fontSize: '1rem',
          fontWeight: '600',
          color: '#8b5cf6',
          marginBottom: '0.5rem'
        }}>
          Your Spiritual Journey
        </h4>
        
        <p style={{
          fontSize: '0.875rem',
          color: '#f1f5f9',
          lineHeight: '1.6'
        }}>
          You've meditated for <strong>{evolution.meditationMinutes} minutes</strong> across{' '}
          <strong>{evolution.meditationSessions} sessions</strong>.
          {evolution.currentStreak > 0 && (
            <> You're on a <strong>{evolution.currentStreak}-day streak</strong>! Keep going!</>
          )}
        </p>
        
        <div style={{
          marginTop: '1rem',
          fontSize: '0.75rem',
          color: '#64748b',
          fontStyle: 'italic'
        }}>
          "The journey of a thousand miles begins with a single step."
        </div>
      </motion.div>
    </div>
  );
};

export default EvolutionTracker;

